import { Pencil, Trash2 } from 'lucide-react'
import { FormProvider } from 'react-hook-form'
import { useDeliveryLocationTypesTable } from '@/hooks/features/delivery-location-type'
import { useDataTable } from '@/hooks/ui'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination-controls'
import { Switch } from '@/components/ui/switch'
import { ActionDropdown, ConfirmDialog, PageHeader } from '@/components/atoms'
import { useBusiness } from '@/contexts/BusinessContext'
import { DeliveryLocationTypeModel } from '@/types'
import { DeliveryLocationTypeFilters } from './DeliveryLocationTypeFilters'
import { DeliveryLocationTypeDialog } from './DeliveryLocationTypeDialog'

type TableColumns = 'name' | 'charge' | 'status' | 'orders_count' | 'actions'

const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = [
    'name',
    'charge',
    'status',
    'orders_count',
    'actions',
]

export const DeliveryLocationTypesTable = () => {
    const {
        loading,
        data,
        pagination,
        formMethods,
        stats,
        mode,
        deliveryLocationTypeDialogOpen,
        editDeliveryLocationType,
        deleteDialogOpen,
        deliveryLocationTypeToDelete,
        createLoading,
        updateLoading,
        deleteLoading,
        setPage,
        setPageSize,
        openCreateDialog,
        openEditDialog,
        closeDialog,
        handleDeleteClick,
        handleDeleteConfirm,
        setDeleteDialogOpen,
        handleCreateDeliveryLocationType,
        handleUpdateDeliveryLocationType,
        toggleStatus,
    } = useDeliveryLocationTypesTable()
    const { formatCurrency } = useBusiness()

    const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
        DeliveryLocationTypeModel.DeliveryLocationType,
        TableColumns
    >({
        columns: [
            {
                accessorKey: 'name',
                columnLabel: () => 'Delivery area',
                cell: (data) => <span className="text-sm font-medium text-ink">{data?.name}</span>,
                hasSorting: true,
            },
            {
                accessorKey: 'charge',
                columnLabel: () => 'Charge',
                cell: (data) => <span className="tabular-nums text-ink">{formatCurrency(data?.charge ?? 0)}</span>,
                classes: () => ({
                    column: 'text-right',
                    cell: 'text-right',
                }),
            },
            {
                accessorKey: 'status',
                columnLabel: () => 'Status',
                cell: (data) => (
                    <div className="flex items-center gap-2">
                        <Badge tone={data?.status === 'active' ? 'success' : 'soft'}>
                            {data?.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                        <Switch
                            aria-label="Active"
                            checked={data?.status === 'active'}
                            onCheckedChange={() => toggleStatus(data)}
                        />
                    </div>
                ),
            },
            {
                accessorKey: 'orders_count',
                columnLabel: () => 'Orders',
                cell: (data) => (
                    <span className="tabular-nums text-ink">{data?.orders_count || 0}</span>
                ),
                classes: () => ({
                    column: 'text-right',
                    cell: 'text-right',
                }),
            },
            {
                accessorKey: 'actions',
                columnLabel: () => 'Actions',
                cell: (data) => (
                    <div className="flex justify-end">
                        <ActionDropdown
                            items={[
                                {
                                    label: 'Edit',
                                    permission: 'delivery_location_types:update',
                                    icon: Pencil,
                                    onClick: () => openEditDialog(data),
                                },
                                {
                                    label: 'Delete',
                                    permission: 'delivery_location_types:delete',
                                    icon: Trash2,
                                    variant: 'destructive',
                                    onClick: () => handleDeleteClick(data),
                                },
                            ]}
                        />
                    </div>
                ),
                classes: () => ({
                    column: 'text-right',
                    cell: 'text-right',
                }),
                isAction: true,
            },
        ],
        visibleColumns: DEFAULT_VISIBLE_COLUMNS,
        disabledColumns: ['actions'],
    })

    return (
        <div className="space-y-4">
            <PageHeader
                size="large"
                title="Delivery areas"
                onAddClick={openCreateDialog}
                addPermission="delivery_location_types:create"
                addButtonText="Add delivery area"
            />

            <Card>
                <CardHeader className="pb-3">
                    <div className="space-y-1">
                        <CardTitle>All delivery areas</CardTitle>
                        <CardDescription>
                            {stats.totalTypes} {stats.totalTypes === 1 ? 'area' : 'areas'}, {stats.activeTypes} active,{' '}
                            {stats.inactiveTypes} inactive
                        </CardDescription>
                    </div>
                    <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                            <Pagination.PageSizeDropdown
                                pageSize={pagination.pageSize}
                                onPageSizeChange={setPageSize}
                            />
                            <FormProvider {...formMethods}>
                                <DeliveryLocationTypeFilters />
                            </FormProvider>
                        </div>
                        <ColumnVisibilityDropdown
                            columns={columns}
                            onColumnVisibilityChange={changeColumnVisibility}
                            disabledColumns={disabledColumns}
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    {!loading && data.length === 0 ? (
                        <NoData
                            title="No delivery areas found"
                            btnText="Add delivery area"
                            openCreateDialog={openCreateDialog}
                            createPermission="delivery_location_types:create"
                        />
                    ) : (
                        <>
                            <DataTable columns={columns} data={data} loading={loading} />
                            <Pagination.Buttons
                                currentPage={pagination.currentPage}
                                totalItems={pagination.totalCount}
                                pageSize={pagination.pageSize}
                                onPageChange={setPage}
                            />
                        </>
                    )}
                </CardContent>
            </Card>

            <DeliveryLocationTypeDialog
                open={deliveryLocationTypeDialogOpen}
                mode={mode}
                editDeliveryLocationType={editDeliveryLocationType}
                closeDialog={closeDialog}
                onCreate={handleCreateDeliveryLocationType}
                onUpdate={handleUpdateDeliveryLocationType}
                loading={createLoading || updateLoading}
            />

            <ConfirmDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                title="Delete delivery area?"
                description={`"${deliveryLocationTypeToDelete?.name}" will be deleted. Existing orders in this area aren't affected, but new orders can't use it.`}
                confirmText={deleteLoading ? 'Deleting…' : 'Delete'}
                onConfirm={handleDeleteConfirm}
                loading={deleteLoading}
                variant="destructive"
            />
        </div>
    )
}
